import { Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import { OpsAuthProvider, useOpsAuth } from "./OpsAuthContext";
import OpsLogin from "./OpsLogin";
import OpsChat from "./OpsChat";
import OpsDMs from "./OpsDMs";
import OpsDashboard from "./OpsDashboard";
import OpsClients from "./OpsClients";
import OpsAccounts from "./OpsAccounts";
import OpsTasks from "./OpsTasks";
import OpsVideo from "./OpsVideo";
import OpsPosts from "./OpsPosts";
import OpsAssistants from "./OpsAssistants";
import { Sparkles, Inbox, LayoutDashboard, Users, Share2, CheckSquare, Film, CalendarClock, UserCog, LogOut } from "lucide-react";

const PRIMARY = [
  { to: "/ops", label: "Asistente IA", icon: Sparkles, end: true },
  { to: "/ops/dms", label: "Mensajes (DMs)", icon: Inbox },
];
const SUPPORT = [
  { to: "/ops/resumen", label: "Resumen", icon: LayoutDashboard },
  { to: "/ops/clientes", label: "Clientes", icon: Users },
  { to: "/ops/cuentas", label: "Cuentas", icon: Share2 },
  { to: "/ops/video", label: "Producción de Video", icon: Film },
  { to: "/ops/publicaciones", label: "Publicaciones", icon: CalendarClock },
  { to: "/ops/tareas", label: "Tareas", icon: CheckSquare },
];
const linkCls = ({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-[#AC3333] text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`;

function Shell({ children }) {
  const { user, logout } = useOpsAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";
  const doLogout = async () => { await logout(); navigate("/ops/login"); };
  return (
    <div className="flex min-h-screen bg-[#1a0707] text-white">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-white/10 bg-[#241010] p-4 md:flex">
        <div className="mb-6 px-2"><div className="font-serif text-lg font-bold">Operaciones</div><div className="text-[11px] text-white/40">Panel interno</div></div>
        <nav className="flex-1 space-y-1">
          {PRIMARY.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`ops-nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`} className={linkCls}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
          <div className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-white/30">Soporte y registros</div>
          {SUPPORT.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`ops-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
          {isSuper && (
            <NavLink to="/ops/asistentes" data-testid="ops-nav-asistentes" className={linkCls}>
              <UserCog className="h-4 w-4" /> Asistentes
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/10 pt-3">
          <div className="px-2 text-xs text-white/40">{user?.name || user?.email}</div>
          <button data-testid="ops-logout" onClick={doLogout} className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/60 hover:bg-white/5"><LogOut className="h-4 w-4" /> Salir</button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden p-5 sm:p-8">{children}</main>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useOpsAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-[#1a0707]"><div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-[#AC3333]" /></div>;
  if (user === false) return <Navigate to="/ops/login" replace />;
  return <Shell>{children}</Shell>;
}

function SuperOnly({ children }) {
  const { user } = useOpsAuth();
  if (user && user.role !== "super_admin") return <Navigate to="/ops" replace />;
  return children;
}

export default function OpsRoot() {
  return (
    <OpsAuthProvider>
      <Routes>
        <Route path="login" element={<OpsLogin />} />
        <Route path="" element={<Protected><OpsChat /></Protected>} />
        <Route path="dms" element={<Protected><OpsDMs /></Protected>} />
        <Route path="resumen" element={<Protected><OpsDashboard /></Protected>} />
        <Route path="clientes" element={<Protected><OpsClients /></Protected>} />
        <Route path="cuentas" element={<Protected><OpsAccounts /></Protected>} />
        <Route path="tareas" element={<Protected><OpsTasks /></Protected>} />
        <Route path="video" element={<Protected><OpsVideo /></Protected>} />
        <Route path="publicaciones" element={<Protected><OpsPosts /></Protected>} />
        <Route path="asistentes" element={<Protected><SuperOnly><OpsAssistants /></SuperOnly></Protected>} />
        <Route path="*" element={<Navigate to="/ops" replace />} />
      </Routes>
    </OpsAuthProvider>
  );
}
